import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { transformResponseHandler } from 'utils/transformResponseHandler';

export const supportTicketApi = createApi({
  reducerPath: 'supportTicketApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_ROUTE}`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.posData?.token;
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  }),
  tagTypes: ['SupportTickets'],
  endpoints: (builder) => ({
    getTickets: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status && params.status !== 'all') queryParams.append('status', params.status);
        if (params.category && params.category !== 'all') queryParams.append('category', params.category);
        if (params.priority && params.priority !== 'all') queryParams.append('priority', params.priority);
        if (params.search) queryParams.append('search', params.search);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.page) queryParams.append('page', params.page);
        if (params.perPage) queryParams.append('perPage', params.perPage);

        const queryString = queryParams.toString();
        return {
          url: `/pos/tickets${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['SupportTickets'],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    getTicketDetail: builder.query({
      query: (id) => ({
        url: `/pos/tickets/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'SupportTickets', id }],
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    createTicket: builder.mutation({
      query: (body) => ({
        url: '/pos/tickets/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SupportTickets'],
      transformResponse: (resp) => transformResponseHandler(resp),
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    replyTicket: builder.mutation({
      query: ({ id, message, attachments }) => ({
        url: `/pos/tickets/${id}/reply`,
        method: 'POST',
        body: { message, attachments },
      }),
      invalidatesTags: (result, error, { id }) => ['SupportTickets', { type: 'SupportTickets', id }],
      transformResponse: (resp) => transformResponseHandler(resp, false),
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    updateTicketStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/pos/tickets/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => ['SupportTickets', { type: 'SupportTickets', id }],
      transformResponse: (resp) => transformResponseHandler(resp),
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
    uploadAttachments: builder.mutation({
      query: (formData) => ({
        url: '/pos/tickets/upload-attachments',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (resp) => transformResponseHandler(resp, false),
      transformErrorResponse: (error) => transformResponseHandler(error),
    }),
  }),
});

export const {
  useGetTicketsQuery,
  useGetTicketDetailQuery,
  useCreateTicketMutation,
  useReplyTicketMutation,
  useUpdateTicketStatusMutation,
  useUploadAttachmentsMutation,
} = supportTicketApi;
